// v4 (spec-v4-structure-and-stage.md 5.3 "Update older looks", 6.5; ticket V4-C4): shots drawn with a character's
// older approved sheet. The marker sits on shots, posters, cast cards and the character page (a warning: never
// folded), and "Update these" opens a sheet that shows the price first (V4-C3's quote) and runs the update only on a
// tap with that price (refused with PRICE_CHANGED when it went up: the sheet then shows the new one).
import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../api";
import { characters, olderGroups, olderMarks, quoteLines, scenesText, totalLine, type CharacterDetail, type OlderQuote } from "../characters";
import { director } from "../director";
import { onJob } from "../session";
import { bump, toast, useRefresh, useStore } from "../store";
import type { Project } from "../types";
import { fmtCredits, Icon, Sheet, Spinner } from "./bits";

/** "⚠ 2 older" on a card or a shot. */
export function OlderBadge({ count, label, className = "" }: { count: number; label?: string; className?: string }) {
  if (count <= 0) return null;
  return (
    <span className={`older-badge ${className}`.trim()} title={`${count} shot${count === 1 ? "" : "s"} drawn with an older look`}>
      <Icon name="alert" size={12} /> {label ?? `${count} older`}
    </span>
  );
}

export type OlderMark = { names: string[]; usedN: number; approvedN: number | null; characterIds: string[] };

/**
 * Per scene of one video: who is drawn there with an older sheet (from each cast member's olderShots). Loads only the
 * characters that have older shots, and reloads when characters or the project change.
 */
export function useOlderMarks(p: Pick<Project, "id"> | null): Map<string, OlderMark> {
  const tick = useRefresh("characters", "project");
  const [details, setDetails] = useState<CharacterDetail[]>([]);
  useEffect(() => {
    if (!p) return;
    let live = true;
    characters
      .ofProject(p.id)
      .then((cast) => Promise.all(cast.filter((c) => c.olderShots > 0).map((c) => characters.get(c.id).catch(() => null))))
      .then((ds) => live && setDetails(ds.filter((d): d is CharacterDetail => !!d)))
      .catch(() => live && setDetails([]));
    return () => {
      live = false;
    };
  }, [p?.id, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => {
    const out = new Map<string, OlderMark>();
    if (!p) return out;
    const marks = olderMarks(details, p.id);
    for (const [sceneId, m] of marks) {
      const ids = details.filter((d) => d.olderShots.some((x) => x.projectId === p.id && x.sceneId === sceneId)).map((d) => d.character.id);
      out.set(sceneId, { ...m, characterIds: ids });
    }
    return out;
  }, [details, p?.id]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** The line on a shot drawn with an older look, with "Update" (the priced sheet). */
export function OlderShotNote({ mark, projectId, sceneId, sceneIndex }: { mark: OlderMark; projectId: string; sceneId?: string; sceneIndex?: number }) {
  const [open, setOpen] = useState(false);
  const [keeping, setKeeping] = useState(false);
  // 2026-11-03 (Keaton's report): happy with the shot as drawn: clear the flag for free, nothing is redrawn
  const keep = async () => {
    setKeeping(true);
    try {
      await characters.keepOlder(projectId, sceneId ? { sceneIds: [sceneId] } : {});
      bump("characters", "project", "projects", "collections");
      toast("Kept as drawn. It no longer counts as an older look.", "good");
      director.context(`The user tapped Keep as drawn on ${sceneIndex ? `scene ${sceneIndex}` : "a shot"}'s older look (free; nothing redrawn). Don't offer to update it.`);
    } catch (e) {
      toast(`Couldn't keep it. ${(e as Error).message}`, "bad");
    } finally {
      setKeeping(false);
    }
  };
  const who = mark.names.length > 1 ? `${mark.names.slice(0, -1).join(", ")} and ${mark.names.at(-1)} are` : `${mark.names[0]} is`;
  const id = mark.characterIds[0];
  return (
    <div className="notice notice-warning older-note" role="status" data-code="OLDER_LOOK">
      <Icon name="alert" size={18} />
      <span className="notice-text">
        {who} drawn with an older sheet here (v{mark.usedN}
        {mark.approvedN ? `, now v${mark.approvedN}` : ""}).
      </span>
      {id && (
        <span className="notice-action">
          <button data-action="older.open" type="button" className="btn btn-quiet btn-small" onClick={() => setOpen(true)}>
            Update
          </button>
          <button data-action="older.keep" type="button" className="btn btn-quiet btn-small" disabled={keeping} onClick={() => void keep()} title="It looks right: keep it as drawn (free)">
            Keep as drawn
          </button>
        </span>
      )}
      {open && id && <UpdateOlderSheet characterId={id} name={mark.names[0] ?? "them"} projectIds={[projectId]} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** The quote for one character's older shots (null while it loads or when there are none). */
export function useOlderQuote(characterId: string | null, count: number, projectIds?: string[]): OlderQuote | null {
  const tick = useRefresh("characters");
  const [q, setQ] = useState<OlderQuote | null>(null);
  const key = projectIds?.join(",") ?? "";
  useEffect(() => {
    if (!characterId || count <= 0) return setQ(null);
    let live = true;
    characters
      .olderQuote(characterId, projectIds)
      .then((x) => live && setQ(x))
      .catch(() => live && setQ(null));
    return () => {
      live = false;
    };
  }, [characterId, count, key, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return q;
}

/** "Update 5 shots to Milo's new look?": the shots by video, the price lines, the total, then one priced button. */
export function UpdateOlderSheet({ characterId, name, projectIds, onClose, onDone }: { characterId: string; name: string; projectIds?: string[]; onClose: () => void; onDone?: () => void }) {
  const balance = useStore((s) => s.balance);
  const [q, setQ] = useState<OlderQuote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () =>
    characters
      .olderQuote(characterId, projectIds)
      .then((x) => {
        setQ(x);
        setError(null);
        return x;
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Couldn't price it.");
        return null;
      });
  useEffect(() => {
    void load();
  }, [characterId, projectIds?.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = q ? olderGroups(q.shots) : [];
  const n = q?.shots.length ?? 0;
  const short = q != null && balance != null && balance < q.credits;
  const go = async () => {
    if (!q) return;
    setBusy(true);
    try {
      const r = await characters.updateOlder(characterId, { ...(projectIds?.length ? { projectIds } : {}), quotedCredits: q.credits });
      r.jobs.forEach(onJob);
      bump("credits", "characters", "project", "projects", "collections");
      toast(`Updating ${n} shot${n === 1 ? "" : "s"}. Finished videos stay as they are.`, "good");
      director.context(`The user tapped Update on ${name}'s ${n} older shots (${q.credits} credits; ${r.jobs.length} jobs started). Finished videos stay as they are; re-render to see the new look. Don't start it again.`);
      onDone?.();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === "PRICE_CHANGED") {
        const was = q.credits;
        const next = await load();
        if (next) setChanged(was);
      } else toast(`Couldn't update them. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={n ? `Update ${n} shot${n === 1 ? "" : "s"} to ${name}'s new look?` : `${name}'s older shots`} onClose={onClose} className="older-sheet">
      <div className="sheet-body">
        {!q && !error && <Spinner label="Pricing it" />}
        {error && <p className="field-error">{error}</p>}
        {q && !n && <p className="muted">Every shot uses {name}'s newest sheet. Nothing to update.</p>}
        {q && n > 0 && (
          <>
            <ul className="older-groups">
              {groups.map((g) => (
                <li key={g.projectId}>
                  <strong>{g.title}</strong>
                  <span>{scenesText(g.scenes)}</span>
                  <span className="muted">{g.what}</span>
                </li>
              ))}
            </ul>
            <dl className="older-lines">
              {quoteLines(q).map((l) => (
                <div key={l.label}>
                  <dt>{l.label}</dt>
                  <dd>{fmtCredits(l.credits)}</dd>
                </div>
              ))}
              <div className="older-total">
                <dt>Total</dt>
                <dd>{totalLine(q.credits, balance)}</dd>
              </div>
            </dl>
            {changed != null && (
              <p className="notice notice-warning" role="alert">
                <span className="notice-text">
                  The price changed from {changed} to {q.credits}. Update for {q.credits}?
                </span>
              </p>
            )}
            <p className="field-hint">Finished videos stay as they are. Re-render to see the new look.</p>
            {short && <p className="field-error">You have {balance} credits. Add some first.</p>}
            <div className="sheet-actions">
              <button type="button" className="btn btn-quiet" onClick={onClose}>
                Not now
              </button>
              <button data-action="older.update" type="button" className="btn btn-primary" disabled={busy || short} onClick={() => void go()} data-autofocus>
                {busy ? "Starting" : `Update · ${fmtCredits(q.credits)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
